import type { LiteralUnion } from "../../helpers/types";
import type { ThemeVariant } from "../../models/system.model";

export const colors = {
  black: "#000",
  blackPrimary: "#2C3137",
  blackSecondary: "#3B434D",
  white: "#fff",
  transparent: "transparent",
  current: "currentColor",
  whiteAlpha50: "rgba(255, 255, 255, 0.04)",
  whiteAlpha100: "rgba(255, 255, 255, 0.06)",
  whiteAlpha200: "rgba(255, 255, 255, 0.08)",
  whiteAlpha300: "rgba(255, 255, 255, 0.16)",
  whiteAlpha400: "rgba(255, 255, 255, 0.24)",
  whiteAlpha500: "rgba(255, 255, 255, 0.36)",
  whiteAlpha600: "rgba(255, 255, 255, 0.48)",
  whiteAlpha700: "rgba(255, 255, 255, 0.64)",
  whiteAlpha800: "rgba(255, 255, 255, 0.80)",
  whiteAlpha900: "rgba(255, 255, 255, 0.92)",
  blackAlpha50: "rgba(0, 0, 0, 0.04)",
  blackAlpha100: "rgba(0, 0, 0, 0.06)",
  blackAlpha200: "rgba(0, 0, 0, 0.08)",
  blackAlpha300: "rgba(0, 0, 0, 0.16)",
  blackAlpha400: "rgba(0, 0, 0, 0.24)",
  blackAlpha500: "rgba(0, 0, 0, 0.36)",
  blackAlpha600: "rgba(0, 0, 0, 0.48)",
  blackAlpha700: "rgba(0, 0, 0, 0.64)",
  blackAlpha800: "rgba(0, 0, 0, 0.80)",
  blackAlpha900: "rgba(0, 0, 0, 0.92)",
  gray50: "#F7FAFC",
  gray100: "#EDF2F7",
  gray200: "#EBEFF5",
  gray300: "#CBD5E0",
  gray400: "#A0AEC0",
  gray500: "#718096",
  gray600: "#4A5568",
  gray700: "#2D3748",
  gray800: "#404752",
  gray900: "#343A42",
  red50: "#FFF5F5",
  red100: "#FED7D7",
  red200: "#FEB2B2",
  red300: "#FC8181",
  red400: "#F56565",
  red500: "#E53E3E",
  red600: "#C73636",
  red700: "#9B2C2C",
  red800: "#822727",
  red900: "#63171B",
  orange50: "#FFFAF0",
  orange100: "#FEEBC8",
  orange200: "#FBD38D",
  orange300: "#F6AD55",
  orange400: "#ED8936",
  orange500: "#DD6B20",
  orange600: "#C05621",
  orange700: "#9C4221",
  orange800: "#7B341E",
  orange900: "#652B19",
  yellow50: "#FFFFF0",
  yellow100: "#FEFCBF",
  yellow200: "#FAF089",
  yellow300: "#F6E05E",
  yellow400: "#ECC94B",
  yellow500: "#D69E2E",
  yellow600: "#B7791F",
  yellow700: "#975A16",
  yellow800: "#744210",
  yellow900: "#5F370E",
  green50: "#F0FFF4",
  green100: "#C6F6D5",
  green200: "#36BB35",
  green300: "#68D391",
  green400: "#48BB78",
  green500: "#38A169",
  green600: "#2F855A",
  green700: "#276749",
  green800: "#22543D",
  green900: "#1C4532",
  blue50: "#ebf8ff",
  blue100: "#bee3f8",
  blue200: "#90cdf4",
  blue300: "#63b3ed",
  blue400: "#4299e1",
  blue500: "#3182ce",
  blue600: "#2b6cb0",
  blue700: "#2c5282",
  blue800: "#2a4365",
  blue900: "#1A365D",
  primary50: "#e5e7f9",
  primary100: "#bec4ef",
  primary200: "#929ce4",
  primary300: "#6674d9",
  primary400: "#4657d1",
  primary500: "#2539c9",
  primary600: "#2133c3",
  primary700: "#1b2cbc",
  primary800: "#1624b5",
  primary900: "#0d17a9",
  purple50: "#C99EFF",
  purple100: "#782FD2",
  purple200: "#5B249E",
  purple300: "#B794F4",
  purple400: "#7310FF",
  purple500: "#421876",
  purple600: "#6B46C1",
  purple700: "#553C9A",
  purple800: "#44337A",
  purple900: "#322659",
};

export const DEFAULT_ACCENTS = [
  "red",
  "orange",
  "yellow",
  "green",
  "purple",
  "blue",
] as const;

export type Accent = LiteralUnion<(typeof DEFAULT_ACCENTS)[number], string>;

export const accents: { [key in ThemeVariant]: { [key in Accent]: string } } = {
  light: {
    red: colors.red400,
    orange: colors.orange400,
    yellow: colors.yellow400,
    green: colors.green400,
    purple: colors.purple400,
    blue: colors.primary400,
  },
  dark: {
    red: colors.red300,
    orange: colors.orange300,
    yellow: colors.yellow300,
    green: colors.green300,
    purple: colors.purple200,
    blue: colors.primary300,
  },
} as const;

export const accentsForeground: { [key in ThemeVariant]: string } = {
  light: colors.white,
  dark: "#EEF2F8",
} as const;
